import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Send, Save, Loader2, FileSpreadsheet, Star, Clock, CheckCircle2, Phone, RotateCcw } from 'lucide-react';
import { formatCurrency, formatDate } from '../client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 ring-slate-200' },
  sent: { label: 'Sent', cls: 'bg-amber-50 text-amber-800 ring-amber-200' },
  call_requested: { label: 'Call requested', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  declined: { label: 'Declined', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

export default function AdminProposalsPanel({ clientId, token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/clients/${clientId}/proposals`, { headers: auth });
    if (r.ok) setProposals(await r.json());
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
    // eslint-disable-next-line
  }, [clientId]);

  const newDraft = () => setEditing({
    title: 'Loan strategy proposal',
    broker_summary: '',
    why_this_suits_goals: '',
    risks_notes: '',
    options: [emptyOption()],
    status: 'draft',
  });

  const editProposal = (p) => setEditing({ ...p, options: (p.options || []).map((o) => ({ ...o })) });

  const remove = async (p) => {
    if (!window.confirm(`Delete proposal "${p.title}"?`)) return;
    await fetch(`${backendUrl}/api/admin/proposals/${p.id}`, { method: 'DELETE', headers: auth });
    reload();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  if (editing) {
    return <ProposalEditor
      clientId={clientId}
      token={token}
      initial={editing}
      onClose={() => setEditing(null)}
      onSaved={async () => { setEditing(null); await reload(); }}
    />;
  }

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{proposals.length} proposal(s)</span>
        <button
          onClick={newDraft}
          data-testid="new-proposal"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline"
        >
          <Plus className="w-3.5 h-3.5" /> New proposal
        </button>
      </div>

      {proposals.length === 0 && (
        <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">
          <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-300" /> No proposals yet.
        </div>
      )}

      <ul className="space-y-2">
        {proposals.map((p) => {
          const badge = STATUS_BADGES[p.status] || STATUS_BADGES.draft;
          const selected = (p.options || []).find((o) => o.id === p.selected_option_id);
          return (
            <li key={p.id} className="rounded-xl ring-1 ring-slate-200 bg-white p-4" data-testid={`admin-proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {(p.options || []).length} option{(p.options || []).length !== 1 ? 's' : ''}
                    {p.created_at && <> · created {formatDate(p.created_at)}</>}
                    {p.sent_at && <> · sent {formatDate(p.sent_at)}</>}
                    {p.approved_at && <> · approved {formatDate(p.approved_at)}</>}
                  </div>
                  {selected && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Client chose: {selected.label} ({selected.lender}) — {Number(selected.interest_rate).toFixed(2)}%
                    </div>
                  )}
                  {p.call_requested_at && p.status !== 'approved' && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-sky-700">
                      <Phone className="w-3.5 h-3.5" /> Call requested — {p.call_request_notes || 'no extra notes'}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] ${badge.cls}`}>{badge.label}</span>
                  <button onClick={() => editProposal(p)} className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100">Edit</button>
                  {p.status === 'draft' && (
                    <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete draft">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProposalEditor({ clientId, token, initial, onClose, onSaved }) {
  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateOption = (idx, patch) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };
  const removeOption = (idx) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  const addOption = () => setForm((f) => ({ ...f, options: [...f.options, emptyOption()] }));

  const save = async (statusOverride) => {
    setBusy(true); setErr('');
    try {
      const payload = { ...form };
      if (statusOverride) payload.status = statusOverride;
      // Coerce numeric fields
      payload.options = payload.options.map((o) => ({
        ...o,
        loan_amount: Number(o.loan_amount) || 0,
        interest_rate: Number(o.interest_rate) || 0,
        comparison_rate: o.comparison_rate ? Number(o.comparison_rate) : null,
        repayment_monthly: o.repayment_monthly ? Number(o.repayment_monthly) : null,
        repayment_fortnightly: o.repayment_fortnightly ? Number(o.repayment_fortnightly) : null,
        loan_term_years: Number(o.loan_term_years) || 30,
        upfront_fees: o.upfront_fees ? Number(o.upfront_fees) : null,
        ongoing_fees: o.ongoing_fees ? Number(o.ongoing_fees) : null,
        cashback: o.cashback ? Number(o.cashback) : null,
        lmi: o.lmi ? Number(o.lmi) : null,
        pros: typeof o.pros === 'string' ? o.pros.split('\n').filter(Boolean) : (o.pros || []),
        cons: typeof o.cons === 'string' ? o.cons.split('\n').filter(Boolean) : (o.cons || []),
      }));

      const isUpdate = !!form.id;
      const url = isUpdate
        ? `${backendUrl}/api/admin/proposals/${form.id}`
        : `${backendUrl}/api/admin/clients/${clientId}/proposals`;
      const method = isUpdate ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: auth, body: JSON.stringify(payload) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not save');
      }
      await onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <RotateCcw className="w-3.5 h-3.5" /> Back
        </button>
        <span className="text-xs text-slate-400">{form.id ? 'Editing proposal' : 'New proposal'}</span>
      </div>

      <Field label="Title" value={form.title} onChange={(v) => update({ title: v })} />
      <Field label="Summary (shown above options)" textarea value={form.broker_summary || ''} onChange={(v) => update({ broker_summary: v })} />
      <Field label="Why this suits the client's goals" textarea value={form.why_this_suits_goals || ''} onChange={(v) => update({ why_this_suits_goals: v })} />
      <Field label="Risks & important notes" textarea value={form.risks_notes || ''} onChange={(v) => update({ risks_notes: v })} />

      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-slate-700">Loan options</span>
          <button onClick={addOption} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add option
          </button>
        </div>
        <div className="space-y-3">
          {form.options.map((o, idx) => (
            <OptionEditor key={idx} idx={idx} option={o} onChange={updateOption} onRemove={() => removeOption(idx)} />
          ))}
        </div>
      </div>

      {err && <div className="rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-800 text-sm px-3 py-2">{err}</div>}

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={() => save('draft')} disabled={busy} className="px-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save draft
        </button>
        <button onClick={() => save('sent')} disabled={busy} data-testid="send-proposal" className="px-4 py-2 text-sm rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium inline-flex items-center gap-1.5">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} <Send className="w-3.5 h-3.5" /> Send to client
        </button>
      </div>
    </div>
  );
}

function OptionEditor({ idx, option, onChange, onRemove }) {
  return (
    <details className="rounded-xl ring-1 ring-slate-200 bg-slate-50/60 p-3" open>
      <summary className="cursor-pointer flex items-center gap-2 list-none text-sm font-medium text-slate-800">
        <span className="flex-1">{option.label || `Option ${idx + 1}`} {option.is_recommended && <Star className="inline w-3.5 h-3.5 text-emerald-500 ml-1" />}</span>
        <button type="button" onClick={onRemove} className="text-rose-500 hover:text-rose-700 p-1" title="Remove">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </summary>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Label" value={option.label} onChange={(v) => onChange(idx, { label: v })} />
        <Field label="Lender" value={option.lender} onChange={(v) => onChange(idx, { lender: v })} />
        <Field label="Loan amount (AUD)" type="number" value={option.loan_amount} onChange={(v) => onChange(idx, { loan_amount: v })} />
        <Field label="Interest rate (% p.a.)" type="number" step="0.01" value={option.interest_rate} onChange={(v) => onChange(idx, { interest_rate: v })} />
        <Field label="Comparison rate (%)" type="number" step="0.01" value={option.comparison_rate || ''} onChange={(v) => onChange(idx, { comparison_rate: v })} />
        <Field label="Loan term (years)" type="number" value={option.loan_term_years || 30} onChange={(v) => onChange(idx, { loan_term_years: v })} />
        <Field label="Monthly repayment" type="number" value={option.repayment_monthly || ''} onChange={(v) => onChange(idx, { repayment_monthly: v })} />
        <Field label="Fortnightly repayment" type="number" value={option.repayment_fortnightly || ''} onChange={(v) => onChange(idx, { repayment_fortnightly: v })} />
        <Field label="Loan type" value={option.loan_type || 'Variable'} onChange={(v) => onChange(idx, { loan_type: v })} placeholder="Variable / Fixed / Split" />
        <Field label="Fixed/variable split" value={option.fixed_variable_split || ''} onChange={(v) => onChange(idx, { fixed_variable_split: v })} placeholder="e.g. 50/50" />
        <Field label="Upfront fees" type="number" value={option.upfront_fees || ''} onChange={(v) => onChange(idx, { upfront_fees: v })} />
        <Field label="Ongoing fees ($/yr)" type="number" value={option.ongoing_fees || ''} onChange={(v) => onChange(idx, { ongoing_fees: v })} />
        <Field label="Cashback" type="number" value={option.cashback || ''} onChange={(v) => onChange(idx, { cashback: v })} />
        <Field label="LMI" type="number" value={option.lmi || ''} onChange={(v) => onChange(idx, { lmi: v })} />
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!option.offset} onChange={(e) => onChange(idx, { offset: e.target.checked })} /> Offset
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!option.redraw} onChange={(e) => onChange(idx, { redraw: e.target.checked })} /> Redraw
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={!!option.is_recommended} onChange={(e) => onChange(idx, { is_recommended: e.target.checked })} /> Broker pick
        </label>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Pros (one per line)" textarea value={Array.isArray(option.pros) ? option.pros.join('\n') : option.pros || ''} onChange={(v) => onChange(idx, { pros: v })} />
        <Field label="Cons (one per line)" textarea value={Array.isArray(option.cons) ? option.cons.join('\n') : option.cons || ''} onChange={(v) => onChange(idx, { cons: v })} />
      </div>
      <Field label="Why recommended" textarea value={option.reason_recommended || ''} onChange={(v) => onChange(idx, { reason_recommended: v })} />
    </details>
  );
}

function Field({ label, value, onChange, type = 'text', textarea, placeholder, step }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-600 mb-1 text-xs uppercase tracking-wide">{label}</span>
      {textarea ? (
        <textarea
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
        />
      ) : (
        <input
          type={type}
          step={step}
          value={value === null || value === undefined ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
        />
      )}
    </label>
  );
}

function emptyOption() {
  return {
    label: '',
    lender: '',
    loan_amount: '',
    interest_rate: '',
    comparison_rate: '',
    repayment_monthly: '',
    repayment_fortnightly: '',
    loan_term_years: 30,
    loan_type: 'Variable',
    offset: false,
    redraw: false,
    fixed_variable_split: '',
    upfront_fees: '',
    ongoing_fees: '',
    cashback: '',
    lmi: '',
    pros: '',
    cons: '',
    reason_recommended: '',
    is_recommended: false,
  };
}
